<template>
	<player class="ui ui--map">
		<div ref="mapWrapper" class="ui__map"></div>
		<slot />
	</player>
</template>

<style lang="sass">
.custom-map-marker
	.undefined
		width: 20px
		height: 20px
		background-color: red
</style>

<script>
import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
import { mapStateNested } from '../../tools/helpers';
import UiBase from './base.vue';
import Leaflet from 'leaflet';
// import '../../tools/L.TileLayer.NoGap'; // temporary fix for https://source.shoutrlabs.com/shoutrlabs/webapps/shoutr.boxx-webclient/issues/250
import 'leaflet/dist/leaflet.css';
import { getValueLanguageConent } from '../../tools/helpers';
import { uiComponents } from '../../tools/viewState';
import { actionTypes, mutationTypes, getterTypes } from '../../types';

function getCoordinates(mapLocation) {
	if (mapLocation.type === 'geoLocation') {
		return mapLocation.geoLocation.coordinates.map(point => {
			if (Array.isArray(point)) return [point[1], point[0]];
			return [point.lat, point.lng];
		});
	}
	if (!mapLocation.type || mapLocation[mapLocation.type].coordinates) {
		console.log("mapLocation faulty", mapLocation.name, mapLocation._id);
	}
	return mapLocation[mapLocation.type].coordinates;
}

function hashCode(inputString) {
	let hash = 0;
	if (inputString.length === 0) return hash;
	for (const char of inputString) {
		hash = (hash << 5) - hash + char.charCodeAt(0);
		hash = hash & hash; // Convert to 32bit integer
	}
	return hash;
}

export default {
	extends: UiBase,
	data() {
		return {
			name: uiComponents.uiMap,
			isDistortableImageMap: false,
			mapDataHash: 0,
			renderedLocations: {},
		};
	},
	computed: {
		...mapGetters([
			'currentExhibition',
			'currentArticle',
			'locationsAlteredSearchResults',
			'mapHidden',
			getterTypes.nextLocationIDs,
		]),
		...mapState(['currentExhibitionID', 'currentArticleID', 'visitedLocationIDs']),
		...mapState({
			tourCurrentID: state => state.tour.tourCurrentID,
			landingExhibit: state => state.dataSourceCFG.global.landingExhibit,
			locationMap: state => state.dataSourceCFG.locationMap,
			globalTriggers: state => state.dataSourceCFG.global.triggers,
			searchUiState: state => state.viewState[uiComponents.uiSearchLocations].uiState,
			currentCoordinate: state => state.geoLocation.currentCoordinate,
			tourCurrent: state => state.tour.current,
			mapData: state => state.customMap.mapData,
			tourLocations: state => state.customMap.tourLocations,
			tourPaths: state => state.customMap.tourPaths,
			tourBounds: state => state.customMap.tourBounds,
		}),
		...mapStateNested('customMap', [
			'exhibitionLocationList',
			'exhibitionPolygonLocations',
			'exhibitLocations',
			'mapLayerToExhibitionId',
			'currentLocationID',
		]),
		exhibitionBounds() {
			const exhibitionPolygonLocation = this.exhibitionPolygonLocations[this.currentExhibitionID];
			const exhibitionBounds =
				exhibitionPolygonLocation &&
				exhibitionPolygonLocation[exhibitionPolygonLocation.type].coordinates &&
				exhibitionPolygonLocation[exhibitionPolygonLocation.type].shape === 'polygon' &&
				Leaflet.polygon(
					exhibitionPolygonLocation[exhibitionPolygonLocation.type].coordinates[0]
				).getBounds();
			return exhibitionBounds || this.mapData.bounds;
		},
		exhibitionCenter() {
			return typeof this.exhibitionBounds.getCenter === 'function'
				? this.exhibitionBounds.getCenter()
				: this.mapData.center;
		},
	},
	methods: {
		...mapActions([
			actionTypes.setExhibition,
			actionTypes.searchShowLocationsByList,
			actionTypes.setCustomMapLocationID,
			actionTypes.geoLocationRequestPermission,
		]),
		...mapMutations([mutationTypes.viewStateSetUiState]),
		clearLayers() {
			this.mapLayers.forEach(layer => layer.remove());
			this.mapLayers = [];
			this.renderedLocations = {};
		},
		renderLocation(mapLocation) {
			if (!mapLocation || !mapLocation.type || mapLocation.type === 'beacon') return;
			if (this.renderedLocations[mapLocation._id]) return; // This prevents double rendering of locations
			const coordinates = getCoordinates(mapLocation);
			const shape = mapLocation[mapLocation.type].shape === 'circle' ? 'marker' : mapLocation[mapLocation.type].shape;
			const title = mapLocation.languageContent
				? getValueLanguageConent(mapLocation.languageContent, 'title', this.$store.state)
				: '';
			const { classes } = mapLocation;
			// Rooms that do not have a lable text in the middle
			const data = {
				polygon: [coordinates, { color: 'transparent' }],
				marker: [
					coordinates[0],
					{
						icon: Leaflet.divIcon({
							className: `custom-map-marker${
								mapLocation._id === this.currentLocationID ? ' custom-map-marker--active' : ''
							}${
								this.visitedLocationIDs.hasOwnProperty(mapLocation._id)
									? ' custom-map-marker--visited'
									: ''
							}`,
							html: `<div data-location-id="${mapLocation._id ||
								''}" class="custom-map-marker__icon ${classes}">${title}</div>`,
						}),
						zIndexOffset: 1000,
					},
				],
				polyline: [coordinates, { className: classes }],
			}[shape];
			const layer = Leaflet[shape](...data);

			if (shape === 'polyline') {
				import(/* webpackChunkName: "leaflet-polylinedecorator" */ 'leaflet-polylinedecorator').then(
					() => {
						var decoratorLayer = Leaflet.polylineDecorator(layer, {
							patterns: [
								{
									offset: 25,
									repeat: 50,
									symbol: L.Symbol.arrowHead({
										pixelSize: 15,
										pathOptions: {
											className: `${classes} marker-direction`,
											fillOpacity: 1,
											weight: 0,
										},
									}),
								},
							],
						}).addTo(this.leafletMap);
						this.mapLayers.push(decoratorLayer);
					}
				);
			}

			this.leafletMap.addLayer(layer);
			this.mapLayers.push(layer);

			// Render the room lable in the middel of the polygon.
			if (shape === 'polygon' && (title || classes)) {
				const data = Object.assign({}, mapLocation, {
					[mapLocation.type]: {
						coordinates: [layer.getBounds().getCenter()],
						shape: 'marker',
					},
				});
				this.renderLocation(data);
			}

			// Add click handler for polygon or marker to open the search and highlight the location
			if (mapLocation._id) {
				const isExhibition = !!mapLocation.exhibitionID;
				// Only add the click handler if the location is an exhibition or has articles or tours.
				if (isExhibition || mapLocation.articles.length || mapLocation.tours.length) {
					layer.on('click', () => {
						if (isExhibition) {
							this.setExhibition({ exhibitionID: mapLocation.exhibitionID, retainModal: true });
						}
						this.setCustomMapLocationID(mapLocation._id);
						this.searchShowLocationsByList({ locationIDs: [mapLocation._id], isExhibition });
					});
				}
			}
			this.renderedLocations[mapLocation._id] = true;
		},
		renderLayers(forceRender) {
			if (!forceRender && !(this.currentExhibition && this.mapReady)) return;

			// Remove all previous drawn layers
			this.clearLayers();

			// Render all locations that are always/globally visible.
			this.globalTriggers.forEach(({ locationID }) => {
				this.renderLocation(this.locationMap[locationID]);
			});

			// If a tour is selected, display all locations of the tour
			// including its own path.
			if (this.tourCurrentID) {
				this.tourLocations[this.tourCurrentID].forEach(locationID => {
					this.renderLocation(this.locationMap[locationID]);
				});
			} else {
				// Render all tour paths
				this.tourPaths.forEach(locationID => {
					this.renderLocation(this.locationMap[locationID]);
				});
				// Render article location markers of the current exhibition.
				(this.exhibitLocations[this.currentExhibitionID] || []).forEach(this.renderLocation);
			}

			// If the exhibition has no polygon location, this is the main location -> show all rooms
			if (!this.exhibitionPolygonLocations[this.currentExhibitionID]) {
				// Render all exhibitions polygon, marker, and polyline locations.
				if (!this.exhibitionLocationList) return;
				this.exhibitionLocationList.forEach(this.renderLocation);
			}
			// The exhibition has a location and is a room.
			else {
				// Zoom map to current exhibiton polygon location bounds.
				const exhibitionPolygonLocation = this.exhibitionPolygonLocations[this.currentExhibitionID];
				// Render all non polygon locations of the current exhibition
				this.exhibitionLocationList
					.filter(
						exhibitionLocation =>
							exhibitionLocation.exhibitionID === this.currentExhibitionID &&
							exhibitionLocation[exhibitionLocation.type].shape !== 'polygon'
					)
					.forEach(this.renderLocation);
				// Render exibitions polygon locations except for the current selected.
				this.exhibitionLocationList
					.filter(
						exhibitionLocation =>
							exhibitionLocation.exhibitionID !== this.currentExhibitionID &&
							exhibitionLocation[exhibitionLocation.type].shape === 'polygon'
					)
					.forEach(this.renderLocation);
				// If the exhibiton has a room polygon and language content
				// render a maker with the text from the languageContent in
				// the center of the room.
				if (exhibitionPolygonLocation && exhibitionPolygonLocation.languageContent) {
					this.renderLocation(
						Object.assign({}, exhibitionPolygonLocation, {
							[exhibitionPolygonLocation.type]: {
								coordinates: [this.exhibitionCenter],
								shape: 'marker',
							},
						})
					);
				}
			}

			// Add relevant map tile layers
			(this.mapData.layers || []).forEach((layer, index) => {
				const exhibitionId = (this.mapLayerToExhibitionId || [])[index];
				if (exhibitionId === this.currentExhibitionID) {
					const mapLayer = this.tileLayers[index];
					this.mapLayers.push(mapLayer);
					mapLayer.addTo(this.leafletMap);
				}
			});
		},
		renderCurrentCoordinate() {
			const { latitude, longitude, accuracy } = this.currentCoordinate;
			if (!accuracy) return;
			if (this.currentCoordinateLayer) {
				this.currentCoordinateLayer.setLatLng([latitude, longitude]);
				this.leafletMap.addLayer(this.currentCoordinateLayer);
				return;
			}
			this.currentCoordinateLayer = Leaflet.marker([latitude, longitude], {
				icon: Leaflet.divIcon({
					className: 'current-location-map-marker',
					html: '<div class="current-location-map-marker__icon"></div>',
				}),
			});
			this.leafletMap.addLayer(this.currentCoordinateLayer);
		},
		prepareMap() {
			const {
				minZoom,
				maxZoom,
				layers,
				attribution,
				distortableImage,
				distortableImageCorners,
				isExhibitionMap,
			} = this.mapData;

			this.mapDataHash = hashCode(JSON.stringify(this.mapData));
			this.isDistortableImageMap = !!distortableImage;
			if (this.leafletMap) this.leafletMap.remove();

			this.leafletMap = Leaflet.map(this.$refs.mapWrapper, {
				crs: Leaflet.CRS[this.mapData.crs || 'Simple'],
				attributionControl: !!this.mapData.attribution,
				zoomSnap: 0.25,
				zoomDelta: 0.25,
				zoomControl: false,
			});

			this.maxZoom = maxZoom;
			this.tileLayers = (layers || []).map(layer =>
				Leaflet.tileLayer(layer.templateUrl, {
					maxZoom,
					attribution,
					// FIXME higher min zoom for tablets
					// (window.innerWidth < 500 ? 2 : 3),
					minZoom: minZoom || 0,
					noWrap: true,
					keepBuffer: 3,
				})
			);
			// Render always visible tile layers. These layers do not have
			// and exhibitionId in `mapLayerToExhibitionId`.
			if (this.mapLayerToExhibitionId.length) {
				this.mapLayerToExhibitionId.forEach((exhibitionID, index) => {
					if (!exhibitionID) this.tileLayers[index].addTo(this.leafletMap);
				});
			} else {
				this.tileLayers.forEach(layer => {
					layer.addTo(this.leafletMap);
				});
			}

			// Set the current exhibition as the last exhibition to prevent zooming,
			// since calling this function always means that a new map was loaded and
			// not that the visible area of the same map was changed.
			if (isExhibitionMap) {
				this.lastExhibitionId = this.currentExhibitionID;
			}

			if (this.isDistortableImageMap) {
				import(/* webpackChunkName: "leaflet-toolbar" */ 'leaflet-toolbar')
					.then(() => {
						return import(
							/* webpackChunkName: "leaflet-distortableimage" */ 'leaflet-distortableimage'
						);
					})
					.then(() => {
						Leaflet.distortableImageOverlay(distortableImage, {
							corners: distortableImageCorners,
							mode: 'lock',
							suppressToolbar: true,
						}).addTo(this.leafletMap);
						this.blockZoomOutDetection = true;
						this.leafletMap.fitBounds(this.exhibitionBounds, { animate: true });
					});
			}

			this.leafletMap.on('zoomend', () => {
				if (this.blockZoomOutDetection) {
					this.blockZoomOutDetection = false;
					this.blockAutoZoom = false;
					this.currentZoomValue = this.leafletMap.getZoom();
				}
				if (
					!isExhibitionMap &&
					this.isOpen &&
					this.landingExhibit &&
					this.currentZoomValue > this.leafletMap.getZoom() &&
					!this.blockZoomOutDetection &&
					!this.tourCurrentID
				) {
					// When manually zooming out the landing exhibition is set which would trigger a
					// zoom to the max bounds of the map. This should be prevented.
					this.blockAutoZoom = true;
					setTimeout(() => {
						this.blockAutoZoom = false;
					}, 1000);
					// Set the landing exhibition, this also shows all rooms and removes all active locations.
					this.setExhibition(this.landingExhibit);
				}
			});

			this.currentZoomValue = null;
			this.leafletMap.on('zoomstart', () => {
				this.currentZoomValue = this.leafletMap.getZoom();
			});

			// clear maxBounds to avoid some issues due to initaliiy hidden map
			// this.leafletMap.setMaxBounds(null);
			this.leafletMap.setView(this.mapData.center, 1, { animate: false });
			this.leafletMap.setMaxBounds(this.mapData.bounds, { animate: false });
			this.renderCurrentCoordinate();
			this.renderLayers(true);
			this.$nextTick(this.setTourMarkerClasses);
			// this.$nextTick(() => {
			setTimeout(() => {
				this.resizeMap();
				window.requestAnimationFrame(() => {
					this.setBounds({ animate: false });
				});
				this.mapReady = true; // The map must be ready before rendering layers.
			}, 500);
		},
		async resizeMap() {
			if (!(this.leafletMap && this.isOpen)) return;

			// Highlight the currently active marker;
			Array.from(this.$refs.mapWrapper.querySelectorAll('.custom-map-marker--active')).forEach($el => {
				$el.classList.remove('custom-map-marker--active');
			});
			if (this.currentLocationID) {
				const $marker = this.$refs.mapWrapper.querySelector(
					`[data-location-id="${this.currentLocationID}"]`
				);
				if ($marker) $marker.parentNode.classList.add('custom-map-marker--active');
			}

			// nextTick for search results and DOM to be updated
			await this.$nextTick();
			await this.$nextTick();

			setTimeout(() => {
				this.leafletMap.invalidateSize({ animate: false, pan: false })
			}, 500);
			// set max bounds after invalidating otherwise the fit will always be
			// animated because of moveend listener added by leaflet
		},
		setBounds(_options) {
			const options = Object.assign({}, { animate: true }, _options);
			if (this.tourCurrentID && this.tourBounds[this.tourCurrentID]) {
				const { maxLat, maxLng, minLat, minLng } = this.tourBounds[this.tourCurrentID];
				const tourBounds = Leaflet.latLngBounds([
					[minLat, minLng],
					[maxLat, maxLng],
				]).pad(0.2);
				// Without $nextTick the bounds are not applied.
				this.$nextTick(() => {
					this.leafletMap.setMaxBounds(tourBounds);
					if (this.lastTourId !== this.tourCurrentID) {
						this.lastTourId = this.tourCurrentID;
						this.leafletMap.fitBounds(tourBounds);
					}
				});
			} else {
				// Fit bounds if the exhibition changed
				if (this.currentExhibitionID !== this.lastExhibitionId) {
					this.lastExhibitionId = this.currentExhibitionID;
					if (!this.isOpen || options.animate === false) {
						// If the map is currently hidden do not animate bound fitting
						this.leafletMap.fitBounds(this.exhibitionBounds, { animate: false });
					} else {
						if (!this.blockAutoZoom && this.currentExhibition.triggers.length) {
							// Prevent potential zoom out to be detected as human zoom out.
							this.blockZoomOutDetection = true;
							// Prevent second triggering of this zoom while it's running.
							this.blockAutoZoom = true;
							this.$nextTick(() => {
								this.leafletMap.flyToBounds(this.exhibitionBounds, {
									animate: true,
									maxZoom: this.maxZoom,
								});
							});
						}
					}
				}
			}
		},
		setTourMarkerClasses() {
			const currentArticleLocations = new Set(
				this.currentArticle.triggers.map(({ locationID }) => locationID)
			);
			Array.from(this.$el.querySelectorAll('[data-location-id]')).forEach($el => {
				$el.parentNode.classList[
					this.visitedLocationIDs.hasOwnProperty($el.getAttribute('data-location-id')) ? 'add' : 'remove'
				]('custom-map-marker--visited');
				$el.parentNode.classList[
					this.nextLocationIDs.has($el.getAttribute('data-location-id')) ? 'add' : 'remove'
				]('custom-map-marker--next-on-tour');
				$el.parentNode.classList[
					currentArticleLocations.has($el.getAttribute('data-location-id')) ? 'add' : 'remove'
				]('custom-map-marker--current');
			});
		},
	},
	beforeCreate() {
		this.mapLayers = [];
		this.currentCoordinateLayer = null;
		this.tileLayers = [];
		this.mapReady = false;
		this.animateResize = false;
		this.lastTourId = null;
		this.isBoundsSet = false;
		this.lastExhibitionId = null;
	},
	watch: {
		currentLocationID(newId, oldId) {
			Array.from(this.$refs.mapWrapper.querySelectorAll('.custom-map-marker--active')).forEach($el => {
				$el.classList.remove('custom-map-marker--active');
			});
			const $old = this.$el.querySelector(`[data-location-id="${oldId}]"`);
			if ($old) {
				$old.parentNode.classList.add('custom-map-marker--active');
			}

			const $new = this.$el.querySelector(`[data-location-id="${newId}"]`);
			if ($new) {
				$new.parentNode.classList.add('custom-map-marker--active');
			}
		},
		mapData() {
			const newHashCode = hashCode(JSON.stringify(this.mapData));
			if (newHashCode !== this.mapDataHash) {
				if (this.isOpen) this.prepareMap();
				else this.newMapAvailable = true;
			}
		},
		isOpen(isOpen) {
			if (!isOpen) return;
			if (this.newMapAvailable) {
				this.prepareMap();
				this.newMapAvailable = false;
			}
			// map gets visible show
			// If the map becomes visible resize it without an animation.
			if (this.mapData.crs) {
				this[actionTypes.geoLocationRequestPermission]();
			}
			this.$nextTick(() => {
				// if (this.mapReady) this.resizeMap({ animate: false });
				this.setTourMarkerClasses();
			});

			if (this.currentLocationID) {
				this.searchShowLocationsByList({
					locationIDs: [this.currentLocationID],
					isExhibition: false,
				});
			}
		},
		currentExhibitionID() {
			// maybe replace with currentArticleID
			this.renderLayers();
			window.requestAnimationFrame(() => {
				this.setBounds();
			});
		},
		tourCurrentID() {
			this.setBounds();
			this.renderLayers();
		},
		searchUiState() {
			this.resizeMap();
		},
		locationsAlteredSearchResults() {
			this.resizeMap();
		},
		currentCoordinate() {
			this.renderCurrentCoordinate();
		},
		currentArticle() {
			this.setTourMarkerClasses();
		},
	},
	activated() {
		if (this.mapReady) this.resizeMap();
		// force update of map data
		else this.prepareMap();
	},
};
</script>
